/**
 * Suivis — optimiser qui je suis. (§D49)
 *
 * Le produit tient en une phrase : montrer, en une grille de vignettes, les
 * comptes que Vincent suit et qui ne publient plus, pour qu'il aille les
 * unfollow **dans X**. Le ratio suivis/followers se répare là-bas, pas ici.
 *
 * Le garde-fou est visible dans l'interface parce qu'il EST le produit : un
 * tap ouvre `x.com/<handle>` dans un onglet, et c'est tout. Aucun bouton de
 * cette page ne touche au graphe social — l'app n'en a pas le droit (aucun
 * scope d'écriture) et ne doit pas donner l'impression de l'avoir. Même le
 * « Je l'ai unfollow » (§D53) ne fait que NOTER ce que Vincent vient de faire
 * dans X.
 *
 * Trois signaux se croisent ici, et le produit est dans leur croisement :
 *   - **Posts** (§D49), gratuit — le compte publie-t-il encore ?
 *   - **Likes** (§D51), payant et à la demande — s'il ne publie plus, lit-il
 *     encore ?
 *   - **Réciprocité** (§D52), quasi gratuit — me suit-il ?
 * « Ne me suivent pas » + « dormants » donne la liste d'unfollow évidente.
 *
 * **Deux vues, un seul tri** (§D54). La SPIRALE est la vue par défaut : le
 * compte le plus pertinent au centre, les suivants en s'éloignant. La GRILLE
 * reste, entière, derrière `?vue=grille`. Les deux lisent EXACTEMENT la même
 * liste `visible` — la spirale est une géométrie, pas une seconde notion de
 * pertinence.
 */

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { avatarVariant } from "../lib/avatar.js";
import {
  ACTIVITY_FILTERS,
  INTERACTION_FILTERS,
  LIKES_BATCH_MAX,
  RECIPROCITY_FILTERS,
  boundedFilters,
  buildProfileUrl,
  compareByMode,
  estimateLikesCheckCost,
  matchesActivityFilter,
  matchesInteractionFilter,
  matchesReciprocityFilter,
  signalFor,
  type ActivityFilter,
  type FollowingMode,
  type InteractionFilter,
  type ReciprocityFilter,
} from "@rm/shared";

import { api, ApiError } from "../lib/api.js";
import { formatAge } from "../components/SwipeCard.js";
import { Button, Card, Empty, Spinner } from "../components/ui.js";
import type { SpiralStats, SpiralTile } from "../components/following-spiral/SpiralView.js";

/**
 * La spirale, en chargement DIFFÉRÉ — elle embarque three (§D48).
 *
 * L'onglet Suivis n'est pas la page d'atterrissage, mais le bundle principal ne
 * doit pas porter un moteur 3D pour autant : `lazy` garde la scène et sa
 * dépendance dans un morceau à part, payé à la première ouverture de l'onglet.
 * L'import de type ci-dessus, lui, s'efface à la compilation.
 */
const SpiralView = lazy(() =>
  import("../components/following-spiral/SpiralView.js").then((m) => ({ default: m.SpiralView })),
);

interface FollowedAccount {
  user_id: string;
  handle: string;
  display_name: string | null;
  avatar_url: string | null;
  /** Dernier tweet connu — décodé du snowflake, jamais lu sur l'API. */
  last_activity_at: string | null;
  /** Ma dernière reply ou interaction Orbit avec ce compte. */
  last_interaction_at: string | null;
  /** Le post le plus récemment liké. PROXY : ce n'est pas la date du like. */
  last_liked_post_at: string | null;
  /** Non nul ⇒ on a payé pour savoir. Nul ⇒ « ? ». */
  likes_checked_at: string | null;
  /** `null` ⇒ jamais croisé — ce n'est PAS un « ne me suit pas ». */
  follows_me: boolean | null;
}

interface SyncState {
  synced_count: number;
  /** Une passe est en cours : le curseur est gardé, elle reprendra. */
  pending: boolean;
  last_sync_at: string | null;
  completed_at: string | null;
  last_error: string | null;
}

interface FollowingResponse {
  following: FollowedAccount[];
  sync: SyncState;
  x: { connected: boolean; scope_ok: boolean };
  /** Le mode Likes est-il servi ? (migration 0005 + scope like.read) */
  likes: {
    available: boolean;
    reason: string | null;
    scope_ok: boolean;
    post_unit_cost_usd: number;
    batch_max: number;
  };
  /** Le filtre de réciprocité est-il servi ? (migration 0006) */
  follows_me: { available: boolean; reason: string | null; checked_at: string | null };
  /** Borne basse des filtres. `null` ⇒ aucune borne. */
  account_created_at: string | null;
}

interface LikesResult {
  checked: number;
  withLikes: number;
  failed: number;
  remaining: number;
  stoppedOnRateLimit: boolean;
}

interface SyncResult {
  synced: number;
  done: boolean;
  stoppedOnBudget: boolean;
  /** La passe a-t-elle été forcée hors budget quotidien ? (§D50) */
  forced: boolean;
}

/** Ce que la passe qui vient de finir a fait, en une phrase. */
function summarize(result: SyncResult): string {
  if (result.forced) {
    return result.done
      ? `Liste complète lue — ${result.synced} compte(s) d'un coup, hors budget du jour.`
      : `${result.synced} compte(s) lu(s), la liste n'est pas finie. Relance « Tout lire maintenant ».`;
  }
  return result.done
    ? `Synchro terminée — ${result.synced} compte(s) lu(s).`
    : `${result.synced} compte(s) lu(s). Le reste au prochain déclenchement.`;
}

/** Ce qu'un lot de vérification de likes vient de faire, en une phrase. */
function summarizeLikes(result: LikesResult): string {
  const parts = [`${result.checked} compte(s) vérifié(s)`];
  if (result.failed > 0) parts.push(`${result.failed} illisible(s)`);

  if (result.stoppedOnRateLimit) {
    return `${parts.join(", ")}. Cadence X atteinte — relance dans quelques minutes.`;
  }
  if (result.remaining > 0) {
    return `${parts.join(", ")} — relance pour les ${result.remaining} suivant(s).`;
  }
  return `${parts.join(", ")}.`;
}

/** Un montant en dollars, à la française. */
function usd(amount: number): string {
  return `$${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type FollowingView = "spirale" | "grille";

/**
 * La vue se retient dans l'URL, pas dans un état local (§D41) : Vincent
 * recharge une adresse depuis son iPhone, il ne re-clique pas une barre. La
 * spirale est le défaut, donc c'est la GRILLE qui laisse une trace — l'adresse
 * nue ouvre la vue d'aujourd'hui, quelle qu'elle devienne.
 *
 * On lit `location` à la main plutôt que `useSearchParams` : le banc d'essai
 * monte cette page HORS d'un routeur (§D41), et un hook de react-router y
 * lèverait.
 */
function viewFromUrl(): FollowingView {
  if (typeof location === "undefined") return "spirale";
  return new URLSearchParams(location.search).get("vue") === "grille" ? "grille" : "spirale";
}

function rememberView(view: FollowingView): void {
  const params = new URLSearchParams(location.search);
  if (view === "grille") params.set("vue", "grille");
  else params.delete("vue");
  const query = params.toString();
  history.replaceState(null, "", query ? `?${query}` : location.pathname);
}

export function FollowingPage({
  onSpiralStats,
}: {
  /** Le banc s'en sert pour son HUD — appels de dessin, atlas (§D54). */
  onSpiralStats?: (stats: SpiralStats) => void;
} = {}) {
  const [data, setData] = useState<FollowingResponse | null>(null);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  const [view, setView] = useState<FollowingView>(viewFromUrl);
  const [activity, setActivity] = useState<ActivityFilter>("all");
  const [interaction, setInteraction] = useState<InteractionFilter>("all");
  const [reciprocity, setReciprocity] = useState<ReciprocityFilter>("all");
  const [mode, setMode] = useState<FollowingMode>("posts");
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  /** Le dépliant de confirmation du « Tout lire maintenant » est-il ouvert ? */
  const [confirmingForce, setConfirmingForce] = useState(false);
  /** Idem pour le lot de vérification des likes, qui dépense lui aussi. */
  const [confirmingLikes, setConfirmingLikes] = useState(false);
  const [checkingLikes, setCheckingLikes] = useState(false);
  const [likesMessage, setLikesMessage] = useState<string | null>(null);
  /** Le compte qu'on vient d'ouvrir dans X, et qu'on propose de noter unfollow. */
  const [justOpened, setJustOpened] = useState<FollowedAccount | null>(null);
  /** Vignettes en cours de disparition — le temps du fondu. */
  const [leaving, setLeaving] = useState<string[]>([]);

  const load = useCallback(() => {
    api
      .get<FollowingResponse>("/following")
      .then((response) => {
        setData(response);
        setError(null);
      })
      .catch((err) =>
        setError({
          message: err instanceof Error ? err.message : "Chargement impossible",
          code: err instanceof ApiError ? err.code : undefined,
        }),
      );
  }, []);

  useEffect(load, [load]);

  /**
   * `now` est figé au rendu plutôt que relu à chaque vignette : 450 appels à
   * `Date.now()` donneraient 450 instants légèrement différents, et le tri
   * comme les seuils doivent se lire contre un instant unique.
   */
  const now = useMemo(() => new Date(), [data]);

  const createdAt = data?.account_created_at ? new Date(data.account_created_at) : null;
  const activityOptions = useMemo(
    () => boundedFilters(ACTIVITY_FILTERS, createdAt, now),
    [data?.account_created_at, now],
  );
  const interactionOptions = useMemo(
    () => boundedFilters(INTERACTION_FILTERS, createdAt, now),
    [data?.account_created_at, now],
  );

  /**
   * Les trois filtres se COMBINENT, et c'est là qu'est le produit : « ne me
   * suivent pas » + « > 1 an sans publier » + mode Likes = les comptes qui ne
   * me rapportent rien, ne me lisent pas et ne me suivent pas.
   *
   * Le filtre d'activité porte sur le signal du MODE COURANT : en mode Likes,
   * « > 6 mois » parle de la dernière chose likée, pas du dernier post.
   */
  const visible = useMemo(() => {
    if (!data) return [];
    return data.following
      .filter(
        (account) =>
          matchesActivityFilter(parse(signalFor(account, mode).at), activity, now) &&
          matchesInteractionFilter(parse(account.last_interaction_at), interaction, now) &&
          matchesReciprocityFilter(account.follows_me, reciprocity),
      )
      // Le tri est FIXE — les plus dormants d'abord, les inconnus en tête, et
      // en mode Likes les non vérifiés avant tout le monde. On le rejoue ici
      // plutôt que de faire confiance à l'ordre du serveur : la même règle, au
      // même endroit que les filtres.
      .sort((a, b) => compareByMode(a, b, mode));
  }, [data, activity, interaction, reciprocity, mode, now]);

  /** Ce qu'un lot vérifierait vraiment : le haut de la grille, tronqué. */
  const batchMax = data?.likes.batch_max ?? LIKES_BATCH_MAX;
  const likesBatch = useMemo(() => visible.slice(0, batchMax), [visible, batchMax]);

  /**
   * Ce que la spirale a besoin de savoir, DANS L'ORDRE DE `visible` — c'est
   * cet ordre-là, et lui seul, qui décide du centre et de l'éloignement.
   *
   * Les vignettes notées « unfollow » sortent de la liste AVANT la spirale, là
   * où la grille les garde le temps du fondu : c'est ce qui fait que la spirale
   * se resserre. La vignette devient un fantôme qui s'éteint sur place pendant
   * que ses cadettes glissent d'un cran (§D54).
   */
  const spiralTiles = useMemo<SpiralTile[]>(
    () =>
      visible
        .filter((account) => !leaving.includes(account.user_id))
        .map((account) => {
          const age = ageBadge(account, mode, now);
          return {
            key: account.user_id,
            handle: account.handle,
            label: account.display_name?.trim() || `@${account.handle}`,
            // L'URL BRUTE : c'est l'atlas qui choisit sa variante CDN, il est
            // le seul à connaître la taille de ses cellules.
            avatarUrl: account.avatar_url,
            badge: age.text,
            dormant: age.dormant,
            followsMe: account.follows_me,
          };
        }),
    [visible, leaving, mode, now],
  );

  /**
   * Le seul geste de cette page : ouvrir le profil dans X. La grille et la
   * spirale passent par ICI — l'app ne touche jamais au graphe social, et ce
   * garde-fou n'a pas à exister en deux exemplaires.
   */
  const openProfile = useCallback((account: FollowedAccount) => {
    window.open(buildProfileUrl(account.handle), "_blank", "noopener,noreferrer");
    setJustOpened(account);
  }, []);

  const openByKey = useCallback(
    (tile: SpiralTile) => {
      const account = visible.find((a) => a.user_id === tile.key);
      if (account) openProfile(account);
    },
    [visible, openProfile],
  );

  /**
   * La barre « je l'ai unfollow » se ferme au premier scroll.
   *
   * En capture, pour attraper le défilement de la coquille comme celui de la
   * fenêtre. Les 300 ms de grâce évitent qu'un reste d'inertie — on vient
   * peut-être de faire défiler la grille pour atteindre la vignette — la
   * referme avant qu'elle ait été lue.
   */
  const openedAt = useRef(0);
  useEffect(() => {
    if (!justOpened) return undefined;
    openedAt.current = Date.now();

    const close = () => {
      if (Date.now() - openedAt.current > 300) setJustOpened(null);
    };
    document.addEventListener("scroll", close, true);
    return () => document.removeEventListener("scroll", close, true);
  }, [justOpened]);

  /**
   * `force` lit la liste entière d'un coup, hors budget du jour (§D50). Il ne
   * part QUE du dépliant de confirmation : la dépense est annoncée avant
   * d'être engagée, c'est tout ce qui sépare ce bouton d'un contournement.
   */
  const sync = async (force = false) => {
    setSyncing(true);
    setMessage(null);
    setConfirmingForce(false);
    try {
      const result = await api.post<SyncResult>(
        "/following/sync",
        force ? { force: true } : undefined,
      );
      setMessage(summarize(result));
      load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Synchro impossible");
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Vérifie les likes du sous-ensemble AFFICHÉ (§D51).
   *
   * On envoie les `user_id` dans l'ordre de la grille, tronqués au lot : cet
   * ordre place déjà en tête ce qu'on veut savoir — les non vérifiés en mode
   * Likes, les plus dormants sinon. Le serveur replafonne de son côté.
   */
  const checkLikes = async () => {
    setCheckingLikes(true);
    setLikesMessage(null);
    setConfirmingLikes(false);
    try {
      const result = await api.post<LikesResult>("/following/check-likes", {
        user_ids: likesBatch.map((account) => account.user_id),
      });
      setLikesMessage(summarizeLikes(result));
      load();
    } catch (err) {
      setLikesMessage(err instanceof Error ? err.message : "Vérification impossible");
    } finally {
      setCheckingLikes(false);
    }
  };

  /**
   * « Je l'ai unfollow » : on note, on ne vérifie pas (§D53).
   *
   * La vignette s'éteint avant de disparaître — sans ce fondu, une grille de
   * cinq colonnes se réarrange d'un coup et on ne sait plus laquelle on vient
   * de traiter.
   */
  const declareUnfollowed = async (account: FollowedAccount) => {
    setJustOpened(null);
    setLeaving((current) => [...current, account.user_id]);
    try {
      await api.post("/following/unfollowed", { user_id: account.user_id });
      window.setTimeout(() => {
        setData((current) =>
          current === null
            ? current
            : {
                ...current,
                following: current.following.filter((a) => a.user_id !== account.user_id),
                sync: { ...current.sync, synced_count: Math.max(0, current.sync.synced_count - 1) },
              },
        );
        setLeaving((current) => current.filter((id) => id !== account.user_id));
      }, LEAVE_MS);
    } catch (err) {
      // Rien n'a été noté : la vignette se rallume plutôt que de mentir.
      setLeaving((current) => current.filter((id) => id !== account.user_id));
      setMessage(err instanceof Error ? err.message : "Impossible de noter l'unfollow");
    }
  };

  if (error) {
    return (
      <div className="px-4 pt-4">
        <Empty
          title={error.code === "migration_missing" ? "Migration à jouer" : "Suivis indisponibles"}
          hint={error.message}
        />
      </div>
    );
  }
  if (!data) return <Spinner />;

  return (
    <div className="flex flex-col gap-4 px-4 pt-4 pb-4">
      <SyncBanner
        state={data.sync}
        scopeOk={data.x.scope_ok}
        connected={data.x.connected}
        syncing={syncing}
        onSync={() => sync()}
        onForce={() => sync(true)}
        confirmingForce={confirmingForce}
        onAskForce={() => setConfirmingForce(true)}
        onCancelForce={() => setConfirmingForce(false)}
        message={message}
      />

      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <FilterSelect
            id="filter-activity"
            label={mode === "likes" ? "Dernier post liké" : "Dernière activité sur X"}
            value={activity}
            options={activityOptions}
            onChange={(v) => setActivity(v as ActivityFilter)}
          />
          <FilterSelect
            id="filter-interaction"
            label="Dernière interaction"
            value={interaction}
            options={interactionOptions}
            onChange={(v) => setInteraction(v as InteractionFilter)}
          />
        </div>

        {data.follows_me.available && (
          <FilterSelect
            id="filter-reciprocity"
            label="Suivi réciproque"
            value={reciprocity}
            options={RECIPROCITY_FILTERS}
            onChange={(v) => setReciprocity(v as ReciprocityFilter)}
          />
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {/* La vue d'abord, le signal ensuite : on choisit COMMENT on
                regarde avant de choisir CE qu'on regarde. */}
            <ViewSwitch
              view={view}
              onChange={(next) => {
                setView(next);
                rememberView(next);
              }}
            />
            <ModeSwitch mode={mode} onChange={setMode} disabled={!data.likes.available} />
          </div>

          {/* Offert dans les DEUX modes, et c'est important : le flux réel est
              de filtrer les dormants en Posts, puis de vérifier si ces
              comptes-là likent encore. En mode Likes le filtre d'activité
              parle déjà des likes, donc le sous-ensemble affiché n'est plus le
              même — le bouton n'aurait pas la même cible. */}
          {data.likes.available && (
            <button
              type="button"
              onClick={() => setConfirmingLikes(true)}
              disabled={checkingLikes || likesBatch.length === 0}
              data-testid="likes-open"
              className="text-xs text-[var(--color-muted)] underline underline-offset-2 disabled:opacity-40"
            >
              {checkingLikes ? "Vérification…" : "Vérifier les likes"}
            </button>
          )}
        </div>

        {mode === "likes" && (
          <p className="text-[11px] leading-snug text-[var(--color-muted)]">
            {data.likes.available
              ? "Date du dernier POST liké — l'API ne donne pas la date du like. Un like d'aujourd'hui sur un vieux thread paraîtra donc ancien."
              : data.likes.reason}
          </p>
        )}

        {/* Même dépliant que « Tout lire maintenant » : ce bouton dépense, donc
            il annonce avant de partir. */}
        {confirmingLikes && (
          <div
            className="flex flex-col gap-2 rounded-lg border border-[var(--color-line)] p-3"
            data-testid="likes-confirm"
          >
            <p className="text-[11px] leading-snug text-[var(--color-muted)]">
              {likesBatch.length} compte(s) ≈ {usd(estimateLikesCheckCost(likesBatch.length, data.likes.post_unit_cost_usd))} — hors budget quotidien.
              {visible.length > likesBatch.length &&
                ` Les ${batchMax} premiers du filtre ; relance pour les ${visible.length - likesBatch.length} suivants.`}
            </p>
            {!data.likes.scope_ok && (
              <Notice tone="warn">Reconnecte X dans Réglages — nouveau scope like.read.</Notice>
            )}
            <div className="flex items-center gap-2">
              <Button onClick={checkLikes} disabled={checkingLikes}>
                {checkingLikes ? "Vérification…" : "Confirmer"}
              </Button>
              <Button onClick={() => setConfirmingLikes(false)} disabled={checkingLikes} variant="ghost">
                Annuler
              </Button>
            </div>
          </div>
        )}

        {likesMessage && <p className="text-[11px] text-[var(--color-muted)]">{likesMessage}</p>}

        <p className="text-[11px] text-[var(--color-muted)] tabular-nums" data-testid="counts">
          {data.sync.synced_count} suivi{data.sync.synced_count > 1 ? "s" : ""} synchronisé
          {data.sync.synced_count > 1 ? "s" : ""} · {visible.length} dans le filtre
        </p>
      </div>

      {visible.length === 0 ? (
        <Empty
          title="Personne dans ce filtre"
          hint={
            data.sync.synced_count === 0
              ? "Lance une synchro pour peupler la liste."
              : "Élargis le filtre : aucun compte suivi n'est aussi dormant que ça."
          }
        />
      ) : view === "spirale" ? (
        /* La spirale déborde de la gouttière de la page : une carte se lit
           bord à bord, et les 16 px de marge la feraient ressembler à un
           encart. Le `-mx-4` annule exactement le `px-4` du conteneur. */
        <div className="-mx-4" data-testid="spiral">
          <Suspense fallback={<div className="h-[320px]"><Spinner /></div>}>
            <SpiralView tiles={spiralTiles} onOpen={openByKey} onStats={onSpiralStats}>
              {/* La barre de retour de X, EN DOM au-dessus du canvas — même
                  composant qu'en grille, posé ailleurs. Il n'y a rien à
                  faire défiler dans une spirale : elle s'y superpose au lieu
                  de la suivre. */}
              {justOpened && (
                <div className="absolute inset-x-3 bottom-3">
                  <UnfollowBar
                    account={justOpened}
                    onConfirm={() => declareUnfollowed(justOpened)}
                    onDismiss={() => setJustOpened(null)}
                  />
                </div>
              )}
            </SpiralView>
          </Suspense>
        </div>
      ) : (
        <ul className="grid grid-cols-5 gap-x-2 gap-y-3" data-testid="grid">
          {visible.map((account) => (
            <FollowTile
              key={account.user_id}
              account={account}
              mode={mode}
              now={now}
              leaving={leaving.includes(account.user_id)}
              onOpen={() => openProfile(account)}
            />
          ))}
        </ul>
      )}

      {/* En GRILLE, la barre vit en bas de la grille plutôt qu'en
          surimpression : rien ne masque les vignettes, et elle disparaît au
          premier scroll comme au tap suivant. */}
      {view === "grille" && justOpened && (
        <UnfollowBar
          account={justOpened}
          onConfirm={() => declareUnfollowed(justOpened)}
          onDismiss={() => setJustOpened(null)}
        />
      )}

      <p className="text-[11px] leading-relaxed text-[var(--color-muted)]">
        Un tap ouvre le profil dans X. C'est là que l'unfollow se fait, à la main :
        l'app ne demande aucun droit d'écriture et ne touche jamais à qui tu suis.
        « Oui » ne fait que le noter ici.
      </p>
    </div>
  );
}

/** Durée du fondu d'une vignette qui s'en va. */
const LEAVE_MS = 260;

/**
 * L'interrupteur de VUE (§D54) — deux segments, comme celui du signal.
 *
 * Même dessin que `ModeSwitch`, à dessein : ce sont deux réglages du même
 * ordre, « comment je regarde » et « quoi je regarde », et rien ne justifie
 * qu'ils ne se ressemblent pas.
 */
function ViewSwitch({
  view,
  onChange,
}: {
  view: FollowingView;
  onChange: (view: FollowingView) => void;
}) {
  const segments: { key: FollowingView; label: string }[] = [
    { key: "spirale", label: "Spirale" },
    { key: "grille", label: "Grille" },
  ];

  return (
    <div className="flex rounded-lg border border-[var(--color-line)] p-0.5" role="group">
      {segments.map((segment) => (
        <button
          key={segment.key}
          type="button"
          onClick={() => onChange(segment.key)}
          data-testid={`view-${segment.key}`}
          aria-pressed={view === segment.key}
          className={`rounded px-2.5 py-1 text-[11px] font-medium transition ${
            view === segment.key
              ? "bg-[var(--color-surface-2)] text-[var(--color-text)]"
              : "text-[var(--color-muted)]"
          }`}
        >
          {segment.label}
        </button>
      ))}
    </div>
  );
}

/**
 * La barre de retour de X : « tu viens d'ouvrir ce profil — tu l'as unfollow ? »
 *
 * Un seul composant pour les deux vues (§D54). En grille elle vit dans le flux,
 * sous les vignettes ; en spirale elle se pose en surimpression au bas du
 * canvas, parce qu'il n'y a pas de « sous la spirale ». Même balisage, même
 * `data-testid`, donc mêmes vérifications.
 */
function UnfollowBar({
  account,
  onConfirm,
  onDismiss,
}: {
  account: FollowedAccount;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2"
      data-testid="unfollow-bar"
    >
      <p className="min-w-0 flex-1 truncate text-[11px] text-[var(--color-muted)]">
        <span className="text-[var(--color-text)]">@{account.handle}</span> ouvert · Je l'ai
        unfollow ?
      </p>
      <button
        type="button"
        onClick={onConfirm}
        data-testid="unfollow-confirm"
        className="rounded bg-[var(--color-surface-2)] px-2 py-1 text-[11px] font-medium"
      >
        Oui
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Fermer"
        className="px-1 text-[13px] leading-none text-[var(--color-muted)]"
      >
        ×
      </button>
    </div>
  );
}

/**
 * L'interrupteur de signal. Deux segments, pas un menu : il n'y a que deux
 * états et ils se comparent — on veut pouvoir faire l'aller-retour à l'œil.
 */
function ModeSwitch({
  mode,
  onChange,
  disabled,
}: {
  mode: FollowingMode;
  onChange: (mode: FollowingMode) => void;
  disabled: boolean;
}) {
  const segments: { key: FollowingMode; label: string }[] = [
    { key: "posts", label: "Posts" },
    { key: "likes", label: "Likes" },
  ];

  return (
    <div className="flex rounded-lg border border-[var(--color-line)] p-0.5" role="group">
      {segments.map((segment) => (
        <button
          key={segment.key}
          type="button"
          onClick={() => onChange(segment.key)}
          disabled={disabled && segment.key === "likes"}
          data-testid={`mode-${segment.key}`}
          aria-pressed={mode === segment.key}
          className={`rounded px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-40 ${
            mode === segment.key
              ? "bg-[var(--color-surface-2)] text-[var(--color-text)]"
              : "text-[var(--color-muted)]"
          }`}
        >
          {segment.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// La vignette
// ---------------------------------------------------------------------------

function FollowTile({
  account,
  mode,
  now,
  leaving,
  onOpen,
}: {
  account: FollowedAccount;
  mode: FollowingMode;
  now: Date;
  leaving: boolean;
  onOpen: () => void;
}) {
  const label = account.display_name?.trim() || `@${account.handle}`;
  const age = ageBadge(account, mode, now);

  return (
    <li
      className={`transition-opacity duration-[260ms] ${leaving ? "opacity-0" : "opacity-100"}`}
      data-testid={leaving ? "tile-leaving" : "tile"}
    >
      {/* L'ouverture du profil est remontée à la page (§D54) : les deux vues
          passent par le même geste, et il n'existe qu'à un seul endroit. */}
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-col items-center gap-1 text-center"
        title={`@${account.handle} — ${describe(account, now)}`}
      >
        <span className="relative block w-full">
          {account.avatar_url ? (
            /* `_200x200` et non le `_normal` (48 px) stocké : une vignette de
               ~73 px CSS fait ~220 px physiques à dpr 3 — cf. lib/avatar.ts. */
            <img
              src={avatarVariant(account.avatar_url, "200x200") ?? undefined}
              alt=""
              loading="lazy"
              className="aspect-square w-full rounded-full bg-[var(--color-surface-2)] object-cover"
            />
          ) : (
            <span className="block aspect-square w-full rounded-full bg-[var(--color-surface-2)]" />
          )}

          {/* La réciprocité, en un point posé en haut à gauche : vert s'il me
              suit, gris sinon, RIEN si on ne l'a jamais croisé. Un point gris
              pour « je ne sais pas » ferait passer un inconnu pour un non, et
              c'est exactement ce que le filtre refuse de faire (§D52). */}
          {account.follows_me !== null && (
            <span
              data-testid={account.follows_me ? "dot-follows" : "dot-not-follows"}
              title={account.follows_me ? "Me suit" : "Ne me suit pas"}
              className={`absolute left-0 top-0 block h-2 w-2 rounded-full border border-[var(--color-ink)] ${
                account.follows_me ? "bg-[var(--color-good)]" : "bg-[var(--color-muted)]"
              }`}
            />
          )}

          {/* L'âge d'activité, posé sur la vignette : c'est l'information qui
              fait trier, elle doit se lire sans quitter la photo des yeux.
              Le liseré à la couleur du fond détache la pastille du rond —
              sans lui, un badge sombre sur une photo sombre se lit comme un
              morceau manquant de l'avatar. */}
          <span
            data-testid={age.unknown ? "badge-unknown" : "badge"}
            className={`absolute -bottom-0.5 right-0 rounded border border-[var(--color-ink)] px-1 py-px text-[9px] font-medium tabular-nums ${
              age.dormant
                ? "bg-[#2f1618] text-[var(--color-bad)]"
                : "bg-[var(--color-surface-2)] text-[var(--color-muted)]"
            }`}
          >
            {age.text}
          </span>
        </span>

        {/* Une seule ligne, tronquée : cinq colonnes sur 390 px ne laissent pas
            la place d'un nom entier, et une grille qui se déforme à chaque nom
            long ne se lit plus comme une grille. */}
        <span className="w-full truncate text-[11px] leading-tight text-[var(--color-muted)]">
          {label}
        </span>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Le bandeau de synchro
// ---------------------------------------------------------------------------

function SyncBanner({
  state,
  scopeOk,
  connected,
  syncing,
  onSync,
  onForce,
  confirmingForce,
  onAskForce,
  onCancelForce,
  message,
}: {
  state: SyncState;
  scopeOk: boolean;
  connected: boolean;
  syncing: boolean;
  onSync: () => void;
  onForce: () => void;
  confirmingForce: boolean;
  onAskForce: () => void;
  onCancelForce: () => void;
  message: string | null;
}) {
  const never = state.last_sync_at === null;

  const headline = never
    ? "Jamais synchronisé"
    : state.pending
      ? "Synchro partielle"
      : `À jour · ${formatDate(state.completed_at ?? state.last_sync_at)}`;

  const hint = never
    ? "Une passe lit MES suivis à $0,010 le compte. Le garde-fou plafonne chaque déclenchement à l'enveloppe du jour."
    : state.pending
      ? "Elle reprendra au prochain déclenchement, budget du jour entamé."
      : `${state.synced_count} compte(s) en base.`;

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{headline}</p>
          <p className="text-[11px] leading-snug text-[var(--color-muted)]">{hint}</p>
        </div>
        <Button onClick={onSync} disabled={syncing} variant="primary">
          {syncing ? "Synchro…" : "Synchroniser"}
        </Button>
      </div>

      {/* UN seul diagnostic à la fois, par ordre de gravité. Un token sans
          `follows.read` produit à la fois l'avertissement de scope et l'erreur
          de la dernière passe : afficher les deux, c'est écrire deux fois la
          même phrase et donner l'impression de deux problèmes. */}
      {diagnostic(connected, scopeOk, state.last_error)}

      {/* Le raccourci, sous le bandeau et seulement quand il reste quelque
          chose à lire. Une passe budgétée avance de ~55 comptes : sur ~450
          suivis, ça fait neuf jours d'attente pour une liste qu'on ne lit
          qu'une fois. Discret parce que ça dépense plus de deux plafonds
          quotidiens — mais offert, parce que c'est SON argent et SON geste. */}
      {(never || state.pending) && (
        <div className="border-t border-[var(--color-line)] pt-3">
          {confirmingForce ? (
            <div className="flex flex-col gap-2" data-testid="force-confirm">
              <p className="text-[11px] leading-snug text-[var(--color-muted)]">
                Lit le reste de la liste d'un coup, hors budget quotidien (~$0,01 par compte).
                Les jobs automatiques du jour passeront probablement en skipped_budget.
              </p>
              <div className="flex items-center gap-2">
                <Button onClick={onForce} disabled={syncing}>
                  {syncing ? "Lecture…" : "Confirmer"}
                </Button>
                <Button onClick={onCancelForce} disabled={syncing} variant="ghost">
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={onAskForce}
              disabled={syncing}
              data-testid="force-open"
              className="text-xs text-[var(--color-muted)] underline underline-offset-2 disabled:opacity-40"
            >
              Tout lire maintenant
            </button>
          )}
        </div>
      )}

      {message && <p className="text-[11px] text-[var(--color-muted)]">{message}</p>}
    </Card>
  );
}

/** Ce qui bloque la synchro, en une ligne — ou rien du tout. */
function diagnostic(connected: boolean, scopeOk: boolean, lastError: string | null) {
  if (!connected) {
    return <Notice tone="warn">Aucun compte X connecté. Réglages → Connecter X.</Notice>;
  }
  if (!scopeOk) {
    return <Notice tone="warn">Reconnecte X dans Réglages — nouveau scope follows.read.</Notice>;
  }
  if (lastError) return <Notice tone="bad">{lastError}</Notice>;
  return null;
}

function Notice({ tone, children }: { tone: "warn" | "bad"; children: React.ReactNode }) {
  const tones = {
    warn: "bg-[#2a2410] text-[var(--color-warn)]",
    bad: "bg-[#2f1618] text-[var(--color-bad)]",
  };
  return <p className={`rounded-lg px-3 py-2 text-[11px] ${tones[tone]}`}>{children}</p>;
}

// ---------------------------------------------------------------------------
// Pièces
// ---------------------------------------------------------------------------

function FilterSelect({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  options: { key: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <label htmlFor={id} className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="truncate text-[11px] text-[var(--color-muted)]">{label}</span>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] px-2 py-1.5 text-xs outline-none focus:border-[var(--color-accent)]"
      >
        {options.map((option) => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const parse = (iso: string | null): Date | null => (iso ? new Date(iso) : null);

/** Au-delà de six mois sans rien publier, la vignette se signale. */
const DORMANT_AFTER_DAYS = 183;

/**
 * La pastille d'âge, dans le signal du mode courant.
 *
 * Trois textes possibles, et il faut les trois : une date, « jamais » (on sait
 * qu'il n'y a rien), et « ? » (on n'a pas payé pour savoir). Confondre les
 * deux derniers ferait passer un compte non vérifié pour un compte mort.
 */
function ageBadge(
  account: FollowedAccount,
  mode: FollowingMode,
  now: Date,
): { text: string; dormant: boolean; unknown: boolean } {
  const signal = signalFor(account, mode);
  if (!signal.known) return { text: "?", dormant: false, unknown: true };
  if (!signal.at) return { text: "jamais", dormant: true, unknown: false };

  const minutes = Math.max(0, (now.getTime() - Date.parse(signal.at)) / 60_000);
  return { text: formatAge(minutes), dormant: minutes / 1440 >= DORMANT_AFTER_DAYS, unknown: false };
}

function describe(account: FollowedAccount, now: Date): string {
  const activity = account.last_activity_at
    ? `actif il y a ${ageBadge(account, "posts", now).text}`
    : "aucune activité connue";
  const likes =
    account.likes_checked_at === null
      ? "likes non vérifiés"
      : account.last_liked_post_at
        ? `dernier post liké ${formatDate(account.last_liked_post_at)}`
        : "aucun like lisible";
  const interaction = account.last_interaction_at
    ? `dernière interaction ${formatDate(account.last_interaction_at)}`
    : "jamais interagi";
  const reciprocity =
    account.follows_me === null ? "réciprocité inconnue" : account.follows_me ? "me suit" : "ne me suit pas";

  return `${activity}, ${likes}, ${interaction}, ${reciprocity}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
